"use client";

import {
  m as motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "framer-motion";
import { useRef, type ReactNode } from "react";

import { Link } from "@/i18n/navigation";

type Props = {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  className?: string;
  linkClassName?: string;
  style?: React.CSSProperties;
  strength?: number;
  ariaLabel?: string;
  target?: string;
  rel?: string;
};

export function MagneticButton({
  children,
  href,
  onClick,
  className,
  linkClassName,
  style,
  strength = 0.35,
  ariaLabel,
  target,
  rel,
}: Props) {
  const prefersReduced = useReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 260, damping: 22, mass: 0.4 });
  const springY = useSpring(y, { stiffness: 260, damping: 22, mass: 0.4 });

  function handleMove(e: React.PointerEvent<HTMLDivElement>) {
    if (prefersReduced) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * strength);
    y.set((e.clientY - cy) * strength);
  }

  function handleLeave() {
    x.set(0);
    y.set(0);
  }

  const content = (
    <motion.div
      ref={ref}
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
      style={{ x: springX, y: springY, display: "inline-flex", ...style }}
      className={className}
    >
      {children}
    </motion.div>
  );

  if (href) {
    const external =
      /^https?:/i.test(href) ||
      href.startsWith("mailto:") ||
      href.startsWith("tel:");
    const isInternal = !external && href.startsWith("/");
    const wrapperClass = linkClassName
      ? `inline-flex ${linkClassName}`
      : "inline-flex";

    if (isInternal) {
      return (
        <Link
          href={href}
          aria-label={ariaLabel}
          className={wrapperClass}
        >
          {content}
        </Link>
      );
    }

    return (
      <a
        href={href}
        aria-label={ariaLabel}
        target={target}
        rel={rel ?? (external && target === "_blank" ? "noopener noreferrer" : undefined)}
        className={wrapperClass}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className="inline-flex bg-transparent p-0"
    >
      {content}
    </button>
  );
}
